import React, { FunctionComponent } from "react";
import {
  Image,
  Linking,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import Button, { Appearance } from "../../components/Button";
import Heading from "../../components/Heading";
import Text from "../../components/Text";
import { PRIVACY_URL, TERMS_URL } from "../../constants";
import { DEV_STORE_ID } from "../../environment";
import { colors, fonts } from "../../theme";
import { Language, useLanguage } from "../../i18n";

const visitSteps = [
  {
    number: "1",
    title: "Your information",
    description: "Confirm your details and photo ID.",
  },
  {
    number: "2",
    title: "Choose services",
    description: "Tell the shop what you are visiting for.",
  },
  {
    number: "3",
    title: "Health and consent",
    description: "Answer only the questions that apply.",
  },
  {
    number: "4",
    title: "Review and sign",
    description: "Check everything before submitting.",
  },
];

const extractStoreId = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const match = trimmed.match(/\/visits\/new(?:-unscheduled)?\/([^/?#]+)/i);
  const candidate = match?.[1] || trimmed;

  try {
    return decodeURIComponent(candidate);
  } catch {
    return candidate;
  }
};

interface Props {
  navigation: any;
}

const WelcomeGuestScreen: FunctionComponent<Props> = ({ navigation }) => {
  const [showLinkEntry, setShowLinkEntry] = React.useState(false);
  const [shopLink, setShopLink] = React.useState("");
  const [linkError, setLinkError] = React.useState("");
  const { language, setLanguage, t } = useLanguage();

  const startFromShopLink = () => {
    const storeId = extractStoreId(shopLink);

    if (!storeId || storeId.includes("/") || storeId.includes(" ")) {
      setLinkError("Enter the shop link or code provided by the shop.");
      return;
    }

    setLinkError("");
    navigation.navigate("NewVisitFlow", { storeId });
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.page}>
          <View style={styles.logoHeader}>
            <View style={styles.languageSelector}>
              {(["en", "es", "fr"] as Language[]).map((option) => (
                <TouchableOpacity
                  key={option}
                  accessibilityLabel={`Change language to ${option}`}
                  accessibilityState={{ selected: language === option }}
                  onPress={() => setLanguage(option)}
                  style={[
                    styles.languageOption,
                    language === option && styles.languageOptionActive,
                  ]}
                >
                  <Text
                    variant="bold"
                    style={[
                      styles.languageText,
                      language === option && styles.languageTextActive,
                    ]}
                  >
                    {option.toUpperCase()}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <Image
              source={require("../../../assets/logo/logo-yellow.png")}
              style={styles.logo}
              resizeMode="contain"
            />
            <Text style={styles.secureLabel}>Secure digital consent</Text>
          </View>

          <View style={styles.hero}>
            <View style={styles.illustrationContainer}>
              <Image
                source={require("../../../assets/illustrations/woman-in-app.png")}
                style={styles.illustration}
                resizeMode="contain"
              />
            </View>

            <Heading size="xl" style={styles.title}>
              Your visit starts here
            </Heading>
            <Text size="l" style={styles.subtitle}>
              Complete your shop paperwork from your phone before your
              procedure.
            </Text>

            <View style={styles.expectationRow}>
              <Text style={styles.expectation}>About 5-8 minutes</Text>
              <Text style={styles.expectationDivider}>|</Text>
              <Text style={styles.expectation}>Have your photo ID ready</Text>
            </View>

            {__DEV__ && (
              <Button
                title="Start at House of Tattoo"
                onPress={() =>
                  navigation.navigate("NewVisitFlow", { storeId: DEV_STORE_ID })
                }
                style={styles.primaryAction}
              />
            )}

            <Button
              title={
                showLinkEntry
                  ? "Hide shop link"
                  : __DEV__
                  ? "I have a shop link"
                  : "Enter shop link"
              }
              appearance={__DEV__ ? Appearance.ghost : Appearance.primary}
              onPress={() => {
                setShowLinkEntry(!showLinkEntry);
                setLinkError("");
              }}
              style={styles.secondaryAction}
            />

            {showLinkEntry && (
              <View style={styles.linkCard}>
                <Text variant="bold" style={styles.linkLabel}>
                  Paste your shop link or code
                </Text>
                <TextInput
                  accessibilityLabel="Shop link or code"
                  autoCapitalize="none"
                  autoCorrect={false}
                  onChangeText={(value) => {
                    setShopLink(value);
                    setLinkError("");
                  }}
                  onSubmitEditing={startFromShopLink}
                  placeholder="https://.../visits/new/..."
                  placeholderTextColor={colors.darkGrey}
                  style={[
                    styles.linkInput,
                    !!linkError && styles.linkInputError,
                  ]}
                  value={shopLink}
                />
                {!!linkError && (
                  <Text style={styles.errorMessage}>{linkError}</Text>
                )}
                <Button
                  title="Continue to shop"
                  disabled={!shopLink.trim()}
                  onPress={startFromShopLink}
                />
              </View>
            )}

            <Text style={styles.qrHelp}>
              Using a shop QR code? Scan it with your phone camera and this page
              will open at the correct shop automatically.
            </Text>
          </View>

          <View style={styles.section}>
            <Heading size="l" style={styles.sectionTitle}>
              What to expect
            </Heading>
            <View style={styles.steps}>
              {visitSteps.map((step) => (
                <View key={step.number} style={styles.stepCard}>
                  <View style={styles.stepNumber}>
                    <Text variant="bold" style={styles.stepNumberText}>
                      {step.number}
                    </Text>
                  </View>
                  <View style={styles.stepCopy}>
                    <Text variant="bold" style={styles.stepTitle}>
                      {t(step.title)}
                    </Text>
                    <Text size="s" style={styles.stepDescription}>
                      {t(step.description)}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>

          <View style={styles.returningCard}>
            <Heading size="m" style={styles.returningTitle}>
              Returning customer?
            </Heading>
            <Text style={styles.returningCopy}>
              Use the new link from your shop and the same email address. We
              will reuse eligible profile information so you do not have to
              start over.
            </Text>
          </View>

          <View style={styles.trustFooter}>
            <Text size="s" style={styles.trustText}>
              Your information is securely shared only with the shop handling
              your visit.
            </Text>
            <View style={styles.legalLinks}>
              <TouchableOpacity onPress={() => Linking.openURL(PRIVACY_URL)}>
                <Text size="s" style={styles.legalLink}>
                  Privacy
                </Text>
              </TouchableOpacity>
              <Text size="s" style={styles.legalDivider}>
                |
              </Text>
              <TouchableOpacity onPress={() => Linking.openURL(TERMS_URL)}>
                <Text size="s" style={styles.legalLink}>
                  Terms
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { backgroundColor: colors.black, flex: 1 },
  scrollContent: { flexGrow: 1, paddingHorizontal: 20, paddingBottom: 30 },
  page: { alignSelf: "center", maxWidth: 760, width: "100%" },
  logoHeader: {
    alignItems: "center",
    borderBottomColor: colors.darkestGrey,
    borderBottomWidth: 1,
    paddingBottom: 18,
    paddingTop: 24,
  },
  languageSelector: {
    alignSelf: "flex-end",
    backgroundColor: colors.darkestGrey,
    borderRadius: 18,
    flexDirection: "row",
    marginBottom: 8,
    padding: 3,
  },
  languageOption: {
    alignItems: "center",
    borderRadius: 14,
    minWidth: 38,
    paddingHorizontal: 8,
    paddingVertical: 5,
  },
  languageOptionActive: { backgroundColor: colors.yellow },
  languageText: { color: colors.midGrey, fontSize: 10, letterSpacing: 0.6 },
  languageTextActive: { color: colors.black },
  logo: { height: 40, width: 150 },
  secureLabel: {
    color: colors.midGrey,
    fontFamily: fonts.medium,
    fontSize: 11,
    letterSpacing: 1.2,
    marginTop: 8,
    textTransform: "uppercase",
  },
  hero: { alignItems: "center", paddingBottom: 32, paddingTop: 28 },
  illustrationContainer: {
    alignItems: "center",
    height: 160,
    justifyContent: "center",
    marginBottom: 12,
  },
  illustration: { height: 180, width: 180 },
  title: { color: colors.white, textAlign: "center" },
  subtitle: {
    color: colors.midGrey,
    marginTop: 12,
    maxWidth: 520,
    textAlign: "center",
  },
  expectationRow: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    marginTop: 16,
  },
  expectation: { color: colors.offWhite, fontSize: 13 },
  expectationDivider: { color: colors.yellow, marginHorizontal: 8 },
  primaryAction: { marginTop: 24 },
  secondaryAction: { marginTop: 10 },
  qrHelp: {
    color: colors.darkGrey,
    fontSize: 13,
    lineHeight: 19,
    marginTop: 16,
    maxWidth: 500,
    textAlign: "center",
  },
  linkCard: {
    backgroundColor: colors.darkestGrey,
    borderRadius: 12,
    marginTop: 16,
    maxWidth: 500,
    padding: 18,
    width: "100%",
  },
  linkLabel: { color: colors.white, marginBottom: 8 },
  linkInput: {
    backgroundColor: colors.white,
    borderColor: "transparent",
    borderRadius: 7,
    borderWidth: 1,
    color: colors.black,
    fontFamily: fonts.regular,
    fontSize: 15,
    marginBottom: 12,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  linkInputError: { borderColor: colors.red, marginBottom: 4 },
  errorMessage: { color: colors.red, fontSize: 12, marginBottom: 10 },
  section: {
    borderTopColor: colors.darkestGrey,
    borderTopWidth: 1,
    paddingVertical: 30,
  },
  sectionTitle: { color: colors.white, marginBottom: 18, textAlign: "center" },
  steps: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  stepCard: {
    alignItems: "flex-start",
    backgroundColor: colors.darkestGrey,
    borderRadius: 10,
    flexDirection: "row",
    marginBottom: 12,
    minWidth: 250,
    padding: 16,
    width: "49%",
  },
  stepNumber: {
    alignItems: "center",
    backgroundColor: colors.yellow,
    borderRadius: 18,
    height: 32,
    justifyContent: "center",
    marginRight: 12,
    width: 32,
  },
  stepNumberText: { color: colors.black },
  stepCopy: { flex: 1 },
  stepTitle: { color: colors.white, marginBottom: 4 },
  stepDescription: { color: colors.midGrey, lineHeight: 18 },
  returningCard: {
    backgroundColor: colors.offWhite,
    borderRadius: 12,
    padding: 20,
  },
  returningTitle: { color: colors.black, marginBottom: 8 },
  returningCopy: { color: colors.darkGrey, lineHeight: 21 },
  trustFooter: { alignItems: "center", paddingTop: 24 },
  trustText: { color: colors.midGrey, maxWidth: 520, textAlign: "center" },
  legalLinks: { flexDirection: "row", marginTop: 10 },
  legalLink: { color: colors.yellow },
  legalDivider: { color: colors.darkGrey, marginHorizontal: 10 },
});

export default WelcomeGuestScreen;
